import React, { useState, useEffect } from 'react';
import { EventFormJsonFields } from '../../../interfaces/Events';
import { Controller, useFormContext } from 'react-hook-form';
import { handleFiles } from '../../../Utils/helperFunctions';
import KenColors from '../../../Helpers/Themes/kenColors';
import { Box, Divider, Paper, Typography } from '@mui/material';
import { AppButton } from '../../AppButton';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { useAppFormContext } from '../../../Helpers/Contexts/formContext';
import KenLoader from '../../../GlobalComponents/KenLoader';
import { useStyles } from '../styles';
import KenTextLabel from '../../../GlobalComponents/KenTextLabel';
import KenIcon from '../../../GlobalComponents/KenIcon';
import Image from '../../../Assets/assetHelper';
import { useSnackbar } from 'notistack';
const FileUpload: React.FunctionComponent<{ field: EventFormJsonFields }> = (props) => {
  const { field } = props;
  const { register, control, getValues } = useFormContext();
  const classes = useStyles();
  const {
    state: { files },
    dispatch,
  } = useAppFormContext();
  const [loading, setLoading] = useState(false);
  const [imageStatus, setImageStatus] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const fileDatas = imageStatus == true && files[`${field?.name}`] ? files[`${field?.name}`].name : field.placeholder;
  const fileHandler = async (e: React.ChangeEvent<HTMLInputElement>, fields: EventFormJsonFields) => {
    setLoading(true);
    setImageStatus(false);

    const selectedFile = e.target.files ? e.target.files[0] : null;
    if (!selectedFile) {
      setLoading(false);
      return;
    }

    // Validate file type
    if (!fields.acceptFiles || !fields.acceptFiles.includes(selectedFile.type)) {
      enqueueSnackbar('Invalid file type. Please upload a PNG or JPEG image.', {
        variant: "error",
        autoHideDuration: 1000
      });
      e.target.value = ''; // Clear the input
      setLoading(false);
      return;
    }

    // Validate file size if maxFileSize is specified
    if (fields?.maxFileSize && selectedFile.size > fields.maxFileSize) {
      const errorMessage = fields.maxFileSizeMessage || `File size must be less than ${Math.round(fields.maxFileSize / (1024 * 1024))}MB`;
      enqueueSnackbar(errorMessage, {
        variant: "error",
        autoHideDuration: 1000
      });
      e.target.value = '';
      setLoading(false);
      return;
    }

    // Legacy file size validation for coverImage (max 1MB)
    if (fields?.description === 'Max image size: 1 MB | Min width: 1028px | Image format: PNG, JPEG and JPG' && selectedFile.size > 1024 * 1024) {
      enqueueSnackbar('Image size should not exceed 1 MB.', {
        variant: "error",
        autoHideDuration: 1000
      });
      e.target.value = '';
      setLoading(false);
      return;
    }

    // Proceed with handling the file
    const data = await handleFiles(e, files, fields.acceptFiles);
    dispatch({
      type: 'updateFiles',
      value: {
        fieldName: fields.name,
        fileData: { data: data, name: selectedFile.name }
      }
    });

    setImageStatus(true);
    setLoading(false);
  };


  const FileComponent: React.FunctionComponent = () => {
    switch (field.type) {
      case 'coverField':
        return (
          <Paper
            style={{
              background: files[`${field?.name}`] ? `url(${files[`${field?.name}`].data})` : getValues(`${field.name}`) ? `url(${getValues(`${field.name}`)})` : KenColors.ClientBlue,
              ...field.fieldStyle,
            }}
            className={classes.cardCoverPhoto}
          >
            <Controller
              control={control}
              render={({ field: { onChange } }) => {
                return (
                  <input
                    accept={field?.accept}
                    id={field.name}
                    onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                      await fileHandler(e, field);
                    }}
                    type="file"
                    style={{ display: 'none' }}
                  />
                );
              }}
              {...register(`${field.name}`)}
            />
            <AppButton
              width="220px"
              background={KenColors.kenWhite}
              className={classes.arrayButton}
              others={{ padding: '12px !important' }}
              variant="contained"
              size="small"
              startIcon={<FileUploadOutlinedIcon />}
            >
              <label htmlFor={field.name}>{files[`${field?.name}`] ? files[`${field?.name}`].name : field.label}</label>
            </AppButton>
            <Typography padding={'0px 0px 10px 0px'} className={classes.cardCoverFooter}>
              <div style={{ borderBottom: '#BFCEFF solid 1px', margin: '0px 20px 10px 20px' }} />
              {field?.description}
            </Typography>
          </Paper>
        );
      case 'upload':
        return (
          <Controller
            control={control}
            render={({ field: { onChange }, fieldState: { error } }) => {
              // console.log('onChange232', files[`${field?.name}`], onChange);

              return (
                <>
                  {field.hideLabel && <KenTextLabel label={field.label} labelClassName={classes.inputLableCustom} required={!!field.validation?.required} />}
                  <div className={classes.customFieldContainer} style={{ margin: '4px 0px 8px 0px', ...field.fieldStyle }}>
                    <input
                      id={field?.id}
                      type="file"
                      accept={field.accept}
                      onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                        await fileHandler(e, field);
                        onChange(e);
                      }}
                      style={{ display: 'none', height: '45px' }}
                    />
                    <Box display={'flex'} alignItems={'center'} gap={'8px'} width={'100%'}>
                      <Box width={'100%'} htmlFor={field?.id} component={'label'} className={classes.customUploadField} border={`1px solid ${error?.message ? KenColors.statusRed : KenColors.grey}`}>
                        <h6 className={classes.customUploadFieldText}>{fileDatas}</h6>
                        <FileUploadOutlinedIcon style={{ height: '26px', color: KenColors.grey }} />
                      </Box>
                      {error?.message && <KenIcon iconType="img" icon={Image.Icons.Error} variant="extraSmall" />}
                    </Box>
                  </div>
                  {error?.message && (
                    <Typography textAlign={'start'} color={KenColors.red2} fontSize={'14px'} fontWeight={'500'}>
                      {error?.message}
                    </Typography>
                  )}
                  {field?.helperText && !error?.message && (
                    <Typography textAlign={'start'} color={KenColors.grey} fontSize={'12px'} fontWeight={'400'} marginTop={'4px'}>
                      {field.helperText}
                    </Typography>
                  )}
                </>
              );
            }}
            {...register(`${field.name}`, { ...field.validation })}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {loading && <KenLoader />}
      <FileComponent />
    </>
  );
};

export default FileUpload;
